"use client";

import React from "react";
import { cn } from "@/lib/utils";

interface CalendarEvent {
    day: "Mon" | "Tue" | "Wed" | "Thu" | "Fri";
    start: number; // Hour (e.g. 9.0 = 09:00, 9.5 = 09:30)
    duration: number; // Hours
    title: string;
    type: "standup" | "ceremony" | "planning" | "other";
}

const events: CalendarEvent[] = [
    // Mon
    { day: "Mon", start: 9, duration: 0.25, title: "Standup", type: "standup" },
    // Wed
    { day: "Wed", start: 9, duration: 0.25, title: "Standup", type: "standup" },
    { day: "Wed", start: 9.5, duration: 1.5, title: "Retrospective", type: "ceremony" },
    // Thu
    { day: "Thu", start: 9, duration: 1, title: "Sprint Planning 1", type: "planning" },
    { day: "Thu", start: 10, duration: 1, title: "Sprint Planning 2", type: "planning" },
    { day: "Thu", start: 13, duration: 1, title: "Sprint Planning 3", type: "planning" },
];

const hours = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const days = ["Mon", "Tue", "Wed", "Thu", "Fri"] as const;

export function TeamCalendar() {
    return (
        <div className="w-full max-w-2xl mx-auto bg-background border rounded-xl overflow-hidden shadow-sm select-none">
            {/* Header */}
            <div className="grid grid-cols-[60px_1fr_1fr_1fr_1fr_1fr] border-b bg-muted/30">
                <div className="p-2 text-xs font-semibold text-muted-foreground border-r flex items-center justify-center">
                    Time
                </div>
                {days.map((day) => (
                    <div key={day} className="p-2 text-xs font-semibold text-center border-r last:border-r-0 relative group">
                        {day}
                        {day === "Thu" && (
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-red-400 text-white text-[10px] px-2 py-0.5 rounded shadow-sm z-10 whitespace-nowrap font-bold">
                                Sprint Start
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Grid */}
            <div className="relative border-b" style={{ height: "440px" }}> {/* 40px per hour * 11 hours? 8-18 is 10 hours. */}
                {/* Background Lines */}
                {hours.map((hour, index) => (
                    <div
                        key={hour}
                        className="absolute w-full border-b border-border/50 text-[10px] text-muted-foreground"
                        style={{ top: `${index * 40}px`, height: "40px" }}
                    >
                        <span className="block -mt-2 ml-2 bg-background px-1 w-fit">{hour}:00</span>
                        {/* Half hour dotted line */}
                        <div className="absolute top-1/2 w-full border-b border-dashed border-border/30"></div>
                    </div>
                ))}

                {/* Vertical Lines */}
                <div className="absolute inset-0 grid grid-cols-[60px_1fr_1fr_1fr_1fr_1fr] pointer-events-none h-full">
                    <div className="border-r border-border/50"></div>
                    <div className="border-r border-border/50"></div>
                    <div className="border-r border-border/50"></div>
                    <div className="border-r border-border/50"></div>
                    <div className="border-r border-border/50"></div>
                    <div></div>
                </div>

                {/* Events */}
                <div className="absolute inset-0 grid grid-cols-[60px_1fr_1fr_1fr_1fr_1fr] pl-[60px]">
                    {days.map((day, dayIndex) => (
                        <div key={day} className="relative h-full border-r last:border-r-0 border-transparent">
                            {events
                                .filter((e) => e.day === day)
                                .map((event, i) => {
                                    const top = (event.start - 8) * 40;
                                    const height = event.duration * 40;
                                    return (
                                        <div
                                            key={i}
                                            className={cn(
                                                "absolute inset-x-1 rounded px-1 py-0.5 text-[10px] font-medium leading-tight text-white shadow-sm overflow-hidden flex flex-col justify-center",
                                                event.type === "standup" && "bg-indigo-600",
                                                event.type === "ceremony" && "bg-purple-700",
                                                event.type === "planning" && "bg-purple-600"
                                            )}
                                            style={{ top: `${top}px`, height: `${height}px` }}
                                        >
                                            {event.title}
                                        </div>
                                    );
                                })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
